/**
 * CostEditPanel (§ Cost/AFE entry) — the **editable** daily cost tab. Lists the
 * day's cost actual lines scoped to `reportId`, each with an Edit trigger
 * (PUT `/reports/{report_id}/costs/{entity_id}`) and a "+ Agregar" trigger
 * (POST `/reports/{report_id}/costs`) that both OPEN a stylized `<Modal>` (the
 * house rule: edits open in a modal, not an inline expanded row). Picklist
 * fields (cost class / cost code / phase / vendor / unit) use the shared
 * `PicklistSelect` (Oxbo-M resolved catalog, submit the entry_key, free-text
 * fallback when a catalog is absent). Money/qty are floats as-entered; the FE
 * never recomputes `total`. Refetches the cost list on every successful write.
 * A 403 surfaces "Solo administradores/operadores".
 */
import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { fmtNum } from "@lib/format";
import {
  useGetReportCostsQuery,
  useCreateReportCostMutation,
  useUpdateReportCostMutation,
  type CostLineOut,
  type CostLineCreate,
} from "./costEntryApi";

function isForbidden(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403
  );
}

/** A blank draft for the add form; well_uwi is seeded from an existing line. */
function emptyDraft(wellUwi: string): CostLineCreate {
  return {
    well_uwi: wellUwi,
    cost_class_entry_key: "",
    cost_code_entry_key: "",
    phase_entry_key: "",
    vendor_entry_key: "",
    afe_ref: "",
    item_description: "",
    qty: null,
    unit_entry_key: "",
    unit_price: null,
    total: null,
    currency: "",
    remarks: "",
  };
}

/** Parse a text field into a float or null (empty → null; keeps as-entered). */
function toNum(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function lineToDraft(line: CostLineOut): CostLineCreate {
  return {
    well_uwi: line.well_uwi,
    sequence_no: line.sequence_no,
    cost_class_entry_key: line.cost_class_entry_key ?? "",
    cost_code_entry_key: line.cost_code_entry_key ?? "",
    phase_entry_key: line.phase_entry_key ?? "",
    vendor_entry_key: line.vendor_entry_key ?? "",
    afe_ref: line.afe_ref ?? "",
    item_description: line.item_description ?? "",
    qty: line.qty,
    unit_entry_key: line.unit_entry_key ?? "",
    unit_price: line.unit_price,
    total: line.total,
    currency: line.currency ?? "",
    remarks: line.remarks ?? "",
  };
}

export function CostEditPanel({ reportId, eventId }: { reportId: string; eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetReportCostsQuery(reportId, {
    skip: !reportId,
  });

  const lines = useMemo(() => data ?? [], [data]);
  // well_uwi for new lines: reuse an existing line's uwi so the add form is
  // pre-filled; if the day has no lines yet the field is entered by hand.
  const wellUwi = lines[0]?.well_uwi ?? "";

  // Modal state: "new" for the add form, an entity_id for an edit, else null.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<CostLineCreate>(emptyDraft(""));

  const [createCost, createState] = useCreateReportCostMutation();
  const [updateCost, updateState] = useUpdateReportCostMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  const set = <K extends keyof CostLineCreate>(k: K, v: CostLineCreate[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  function openAdd() {
    setDraft(emptyDraft(wellUwi));
    setEditing("new");
  }
  function openEdit(line: CostLineOut) {
    setDraft(lineToDraft(line));
    setEditing(line.entity_id);
  }

  const canSave = draft.well_uwi.trim() !== "" && !busy;

  async function submit() {
    try {
      if (editing === "new") {
        await createCost({ reportId, body: draft }).unwrap();
      } else if (editing) {
        await updateCost({ reportId, entityId: editing, body: draft }).unwrap();
      }
      setEditing(null);
      void refetch();
    } catch {
      // Error surfaced in the modal footer; keep it open.
    }
  }

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="card" data-event={eventId}>
        <div className="panel-head-row">
          <h3>{t("costEntry.title")}</h3>
          <button type="button" className="btn primary" onClick={openAdd}>
            {t("costEntry.add")}
          </button>
        </div>
        <p className="subtitle" style={{ marginTop: 0 }}>
          {t("costEntry.subtitle")}
        </p>

        {lines.length === 0 ? (
          <div className="empty-state">{t("costEntry.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("costEntry.col.seq")}</th>
                  <th>{t("costEntry.col.costClass")}</th>
                  <th>{t("costEntry.col.costCode")}</th>
                  <th>{t("costEntry.col.phase")}</th>
                  <th>{t("costEntry.col.vendor")}</th>
                  <th>{t("costEntry.col.afeRef")}</th>
                  <th>{t("costEntry.col.description")}</th>
                  <th className="num">{t("costEntry.col.qty")}</th>
                  <th>{t("costEntry.col.unit")}</th>
                  <th className="num">{t("costEntry.col.unitPrice")}</th>
                  <th className="num">{t("costEntry.col.total")}</th>
                  <th>{t("costEntry.col.currency")}</th>
                  <th>{t("costEntry.col.remarks")}</th>
                  <th>{t("costEntry.col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.entity_id} data-owner="p">
                    <td className="num tab-nums">{line.sequence_no ?? "—"}</td>
                    <td>{line.cost_class_entry_key ?? "—"}</td>
                    <td>{line.cost_code_entry_key ?? "—"}</td>
                    <td>{line.phase_entry_key ?? "—"}</td>
                    <td>{line.vendor_entry_key ?? "—"}</td>
                    <td>{line.afe_ref ?? "—"}</td>
                    <td style={{ color: "var(--dim)", maxWidth: 220 }}>
                      {line.item_description ?? "—"}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(line.qty, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td>{line.unit_entry_key ?? "—"}</td>
                    <td className="num tab-nums">
                      {fmtNum(line.unit_price, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(line.total, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td>{line.currency ?? "—"}</td>
                    <td style={{ color: "var(--dim)", maxWidth: 180 }}>{line.remarks ?? "—"}</td>
                    <td>
                      <button type="button" className="btn" onClick={() => openEdit(line)}>
                        {t("costEntry.edit")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("costEntry.add") : t("costEntry.edit")}
        onClose={() => setEditing(null)}
        onConfirm={submit}
        busy={busy}
        canConfirm={canSave}
        confirmLabel={busy ? t("costEntry.saving") : t("costEntry.save")}
        cancelLabel={t("costEntry.cancel")}
        footerNote={
          error ? (isForbidden(error) ? t("costEntry.forbidden") : t("costEntry.error")) : null
        }
      >
        <div className="modal-grid">
          <div className="field">
            <label htmlFor="cost-uwi">{t("costEntry.field.wellUwi")}</label>
            <input
              id="cost-uwi"
              type="text"
              value={draft.well_uwi}
              onChange={(e) => set("well_uwi", e.target.value)}
            />
          </div>
          <PicklistSelect
            id="cost-class"
            categoryKey="cost_classes"
            label={t("costEntry.col.costClass")}
            value={draft.cost_class_entry_key ?? ""}
            onChange={(v) => set("cost_class_entry_key", v)}
          />
          <PicklistSelect
            id="cost-code"
            categoryKey="cost_codes"
            label={t("costEntry.col.costCode")}
            value={draft.cost_code_entry_key ?? ""}
            onChange={(v) => set("cost_code_entry_key", v)}
          />
          <PicklistSelect
            id="cost-phase"
            categoryKey="activity_phases"
            label={t("costEntry.col.phase")}
            value={draft.phase_entry_key ?? ""}
            onChange={(v) => set("phase_entry_key", v)}
          />
          <PicklistSelect
            id="cost-vendor"
            categoryKey="vendors"
            label={t("costEntry.col.vendor")}
            value={draft.vendor_entry_key ?? ""}
            onChange={(v) => set("vendor_entry_key", v)}
          />
          <div className="field">
            <label htmlFor="cost-afe">{t("costEntry.col.afeRef")}</label>
            <input
              id="cost-afe"
              type="text"
              value={draft.afe_ref ?? ""}
              onChange={(e) => set("afe_ref", e.target.value)}
            />
          </div>
          <div className="field modal-span-all">
            <label htmlFor="cost-desc">{t("costEntry.col.description")}</label>
            <input
              id="cost-desc"
              type="text"
              value={draft.item_description ?? ""}
              onChange={(e) => set("item_description", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cost-qty">{t("costEntry.col.qty")}</label>
            <input
              id="cost-qty"
              type="number"
              value={draft.qty ?? ""}
              onChange={(e) => set("qty", toNum(e.target.value))}
            />
          </div>
          <PicklistSelect
            id="cost-unit"
            categoryKey="units"
            label={t("costEntry.col.unit")}
            value={draft.unit_entry_key ?? ""}
            onChange={(v) => set("unit_entry_key", v)}
          />
          <div className="field">
            <label htmlFor="cost-price">{t("costEntry.col.unitPrice")}</label>
            <input
              id="cost-price"
              type="number"
              value={draft.unit_price ?? ""}
              onChange={(e) => set("unit_price", toNum(e.target.value))}
            />
          </div>
          <div className="field">
            <label htmlFor="cost-total">{t("costEntry.col.total")}</label>
            <input
              id="cost-total"
              type="number"
              value={draft.total ?? ""}
              onChange={(e) => set("total", toNum(e.target.value))}
            />
          </div>
          <div className="field">
            <label htmlFor="cost-curr">{t("costEntry.col.currency")}</label>
            <input
              id="cost-curr"
              type="text"
              value={draft.currency ?? ""}
              onChange={(e) => set("currency", e.target.value)}
            />
          </div>
          <div className="field modal-span-all">
            <label htmlFor="cost-remarks">{t("costEntry.col.remarks")}</label>
            <input
              id="cost-remarks"
              type="text"
              value={draft.remarks ?? ""}
              onChange={(e) => set("remarks", e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </QueryState>
  );
}
